<script setup lang="ts">
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'

const props = withDefaults(
  defineProps<{
    modelValue?: any
    items?: any[]
  }>(),
  {
    items: () => [],
  },
)
const emit = defineEmits<(e: 'update:modelValue', value: any) => void>()

const query = ref('')

const filteredItems = computed(() =>
  query.value === ''
    ? props.items
    : props.items.filter((item) =>
        item.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')),
      ),
)
</script>

<template>
  <Combobox
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <Float
      flip
      placement="bottom-start"
      :offset="2"
      enter="transition duration-200 ease-out"
      enter-from="scale-95 opacity-0"
      enter-to="scale-100 opacity-100"
      leave="transition duration-150 ease-in"
      leave-from="scale-100 opacity-100"
      leave-to="scale-95 opacity-0"
      @hide="query = ''"
    >
      <ComboboxButton as="div">
        <slot />
      </ComboboxButton>
      <ComboboxOptions
        class="flex flex-col w-64 pb-1 my-2 text-base bg-white rounded-lg shadow-lg overflow-hidden max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none md:text-sm"
      >
        <div
          class="sticky top-0 px-2 pt-2 bg-white z-10"
        >
          <div
            class="i-heroicons-solid-search pointer-events-none absolute top-[1.1rem] left-4 text-gray-400"
          />
          <ComboboxInput
            class="appearance-none block w-full px-2 py-2 pl-8 border text-sm border-gray-300 text-gray-900 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            @change="query = $event.target.value"
          />
        </div>
        <div
          v-if="filteredItems.length === 0 && query !== ''"
          class="cursor-default select-none relative py-2 pt-4 px-4 text-gray-700"
        >
          Nothing found.
        </div>
        <div class="flex-grow overflow-y-auto pt-2">
          <ComboboxOption
            v-for="item in filteredItems"
            :key="item.id"
            v-slot="{ selected, active }"
            as="template"
            :value="item"
          >
            <li
              class="cursor-default select-none relative py-2 px-4 text-gray-900"
              :class="{
                'bg-gray-100': active,
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                {{ item.name }}
              </span>
            </li>
          </ComboboxOption>
        </div>
      </ComboboxOptions>
    </Float>
  </Combobox>
</template>
